import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { cn } from "@/lib/utils";
import styles from "./MessageContent.module.css";

interface MessageContentProps {
  content: string;
  className?: string;
}

export const MessageContent: React.FC<MessageContentProps> = ({ content, className }) => {
  return (
    <div className={cn(styles.markdown, "text-sm", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
        // Customize rendering of specific elements
        a: ({ ...props }) => (
          <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline" />
        ),
        code: ({ className, children, ...props }: React.HTMLAttributes<HTMLElement> & { node?: { position?: unknown } }) => {
          const inline = !props.node?.position;
          const match = /language-(\w+)/.exec(className || "");
          const language = match ? match[1] : "";
          
          if (inline) {
            return (
              <code className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-sm font-mono" {...props}>
                {children}
              </code>
            );
          }
          
          return (
            <div className="relative">
              {language && (
                <div className="absolute top-0 right-0 px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
                  {language}
                </div>
              )}
              <pre className="overflow-x-auto rounded-lg bg-gray-100 dark:bg-gray-900 p-4">
                <code className="text-sm font-mono" {...props}>
                  {children}
                </code>
              </pre>
            </div>
          );
        },
        pre: ({ children }) => <>{children}</>,
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic my-4">
            {children}
          </blockquote>
        ),
        ul: ({ children }) => (
          <ul className="list-disc list-inside space-y-1 my-2">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal list-inside space-y-1 my-2">{children}</ol>
        ),
        li: ({ children }) => (
          <li className="ml-4">{children}</li>
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto my-4">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              {children}
            </table>
          </div>
        ),
        thead: ({ children }) => (
          <thead className="bg-gray-50 dark:bg-gray-800">{children}</thead>
        ),
        tbody: ({ children }) => (
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {children}
          </tbody>
        ),
        th: ({ children }) => (
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="px-4 py-2 whitespace-nowrap text-sm">{children}</td>
        ),
        h1: ({ children }) => (
          <h1 className="text-2xl font-bold mt-4 mb-2">{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-xl font-bold mt-3 mb-2">{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-lg font-semibold mt-2 mb-1">{children}</h3>
        ),
        h4: ({ children }) => (
          <h4 className="text-base font-semibold mt-2 mb-1">{children}</h4>
        ),
        h5: ({ children }) => (
          <h5 className="text-sm font-semibold mt-1 mb-1">{children}</h5>
        ),
        h6: ({ children }) => (
          <h6 className="text-sm font-semibold mt-1 mb-1">{children}</h6>
        ),
        p: ({ children }) => (
          <p className="mb-2">{children}</p>
        ),
        hr: () => (
          <hr className="my-4 border-t border-gray-200 dark:border-gray-700" />
        ),
        // Task list support for GFM
        input: ({ type, checked, ...props }) => {
          if (type === "checkbox") {
            return (
              <input
                type="checkbox"
                checked={checked}
                disabled
                className="mr-2"
                {...props}
              />
            );
          }
          return <input type={type} {...props} />;
        },
      }}
    >
      {content}
    </ReactMarkdown>
    </div>
  );
};